import type { AnswerAndData } from 'shared/definitions/answer-and-data';
import type { ClefName } from 'shared/definitions/musicTheory/clefs';
import type { NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import type { IntervalName } from 'shared/definitions/musicTheory/intervals';
import type Interval from '../uTheory.Notation/interval';
import type Clef from '../uTheory.Notation/clef';
import type NoteHolder from '../uTheory.Notation/noteHolder';
import type Note from '../uTheory.Notation/note';
import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions } from './interactivePrototype';

import * as Notation from '../notation';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import SkillCalculator from '../uTheory.Skills/skillCalculator';

interface GenericIntervalConstructionOptions extends InteractiveOptions {
  lowestNote?: string | Note;
  highestNote?: string | Note;
  mustFitSingleClef?: boolean;
}

export type GenericIntervalConstructionQuestion = {
  note: Note;
  note2: Note;
  interval: Interval;
  clef: ClefName;
  answer: string;
  text: (string | number)[];
  skills: string;
};

class GenericIntervalConstruction extends InteractivePrototype {
  declare questions: GenericIntervalConstructionQuestion[];

  declare currentQuestion: GenericIntervalConstructionQuestion;

  declare options: GenericIntervalConstructionOptions;

  skillCalculator!: SkillCalculator;

  maxQuestions!: number;

  clef!: Clef;

  questionHolder!: NoteHolder;

  noteHolder!: NoteHolder;

  note: Note | undefined;

  interval: Interval | undefined;

  answerAttempts: number;

  goalMidi: number | undefined;

  constructor(displayDiv: HTMLDivElement, options: GenericIntervalConstructionOptions) {
    super(displayDiv, options);
    this.targetSpeedFromSkills = ['generic_intervals'];
    this.answerAttempts = 0;
    this.settings = {
      instructions: {
        ariaLive: 'alert',
        ariaLiveRegion: 1,
      },
      editor: {
        fixedWidth: 180,
        ariaLive: 'polite',
        ariaTitle: {
          barline: false,
          keySignature: false,
          timeSignature: false,
          rhythm: false,
        },
      },
      letters: {
        letterButtons: false,
        checkButton: true,
      },
      checkMark: {
        rightAnswerLength: 1,
        rightAnswerInOutRatio: 0.7,
        wrongAnswerLength: 1.0,
        wrongAnswerInOutRatio: 0.3,
      },
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };

    this.layout = {
      instructions: { percent: 30, noWrap: true },
      editor: { percent: 50 },
      letters: { percent: 20 },
    };

    this.init(displayDiv, options);
  }

  init(displayDiv: HTMLDivElement, options: GenericIntervalConstructionOptions): Promise<any> {
    if (options.lowestNote === undefined) {
      options.lowestNote = 'C2';
    }

    if (options.highestNote === undefined) {
      options.highestNote = 'C6';
    }

    this.questionPrompt = 'Write {question} <strong>{question}</strong>.';
    if (!this.options.testMode)
      this.questionPrompt += '<br /><small>{question} of {question}</small>';
    this.skillCalculator = new SkillCalculator();
    return super.init(displayDiv, options);
  }

  generateQuestions(): void {
    const options = this.options;
    this.questions = this.getQuestions(options);
    this.maxQuestions = this.questions.length;

    // console.log('Questions from options:', options.questions);
    // console.log('Questions generated from options:', this.questions);
  }

  newCheckpointQuestion(questionString: string): Promise<GenericIntervalConstructionQuestion> {
    return this.loadingPromise.then(() => {
      this.questions = this.generateManualQuestions({
        ...this.options,
        questions: [questionString],
      });
      this.newQuestion();
      // this.resize();
      return Promise.resolve(this.currentQuestion);
    });
  }

  getQuestions(options: GenericIntervalConstructionOptions): GenericIntervalConstructionQuestion[] {
    return this.optionsTrue('drill')
      ? this.generateDrillQuestions(options)
      : this.generateManualQuestions(options);
  }

  generateManualQuestions(
    options: GenericIntervalConstructionOptions
  ): GenericIntervalConstructionQuestion[] {
    const questions: { note: Note; interval: Interval }[] = (options.questions || []).map(
      (question: string): { note: Note; interval: Interval } => {
        const questionParts = question.split('/');
        const note = new Notation.Note(questionParts[0] as NoteName);
        const interval = new Notation.Interval(questionParts[1] as IntervalName);
        return { note, interval };
      }
    );
    return questions.map(
      (
        question: { note: Note; interval: Interval },
        index: number,
        allQuestions: { note: Note; interval: Interval }[]
      ): GenericIntervalConstructionQuestion => this.processQuestion(question, index, allQuestions)
    );
  }

  generateDrillQuestions(
    options: GenericIntervalConstructionOptions
  ): GenericIntervalConstructionQuestion[] {
    const generator = new QuestionGenerator();
    const maxQuestions = options.maxQuestions;
    options.mustFitSingleClef = true;

    const questions: { note: Note; interval: Interval }[] = generator.generate(
      options.skills,
      maxQuestions,
      options
    );

    return questions.map(
      (
        question: { note: Note; interval: Interval },
        index: number,
        allQuestions: { note: Note; interval: Interval }[]
      ): GenericIntervalConstructionQuestion => this.processQuestion(question, index, allQuestions)
    );
  }

  processQuestion(
    question: {
      note: Note;
      interval: Interval;
    },
    index: number,
    allQuestions: { note: Note; interval: Interval }[]
  ): GenericIntervalConstructionQuestion {
    const { note, interval } = question;
    const note2 = note.getNoteAt(interval) as Note;
    const clef = this.getClef(note, note2);
    // const skills = this.calculateSkill(interval);
    const skills = this.calculateSkill();

    const intervalText = interval.longGenericName();
    const ascDescending = interval.direction > 0 ? 'ascending' : 'descending';
    const aOrAn = interval.direction > 0 && interval.genericInterval > 1 ? 'an' : 'a';

    const directedIntervalText =
      interval.genericInterval === 1 ? 'unison' : `${ascDescending} ${intervalText}`;

    const text = [aOrAn, directedIntervalText, `${index + 1}`, allQuestions.length];

    return {
      note,
      note2,
      interval,
      clef,
      answer: '',
      text,
      skills,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  getClef(note: Note, note2: Note): ClefName {
    const clefs: ClefName[] = [];
    note.prefersClef().forEach((potentialClef: ClefName): void => {
      if (note2.prefersClef().indexOf(potentialClef) !== -1) {
        clefs.push(potentialClef);
      }
    });

    const clef = clefs[Math.floor(Math.random() * clefs.length)];

    return clef!;
  }

  initializeMusic(): void {
    const clef = new Notation.Clef('treble');
    const staff = new Notation.Staff(clef);
    const keySig = new Notation.KeySignature('C', 'Major');
    const note = new Notation.Note();
    const noteHolderOptions = {
      pitch: true,
      accidental: false,
      deleteable: false,
      selectable: true,
    } as const;
    const questionHolder = new Notation.NoteHolder(note, 'h');
    const noteHolder = new Notation.NoteHolder('', 'h');

    this.clef = clef;
    this.questionHolder = questionHolder;
    this.noteHolder = noteHolder;
    // this.noteHolder.ghostIfEmpty = true; No atribute ghostIfEmpty
    this.noteHolder.editable = new Notation.Editable(this.noteHolder, noteHolderOptions);

    const measure = new Notation.Measure(this.questionHolder, this.noteHolder);
    const voice = new Notation.Voice(measure);
    voice.setStaff(staff);

    this.music = new Notation.Music(staff, voice, keySig);
  }

  // eslint-disable-next-line class-methods-use-this
  calculateSkill(): string {
    return 'generic_intervals';
  }

  newQuestion(): void {
    super.newQuestion();
    this.progressTracker!.start(this.targetSpeed);

    const question = this.questions[this.questionNumber];
    const clef = question!.clef;
    const note = question!.note;
    const interval = question!.interval;

    this.editor!.deselectAll();

    note.accidental = '';
    this.note = note;
    this.interval = interval;

    this.questionHolder.notes[0] = note;
    this.noteHolder.remove(this.noteHolder.notes[0]!);
    this.noteHolder.editable!.selected = false;
    this.clef.setName(clef);
    this.editor!.renderer.render();

    this.wrongAnswerCount = 0;
  }

  addListeners(): void {
    this.letterPanel!.addCheckCallback(this.check, this);
  }

  clearWarning(): void {
    this.checkMark!.off();
  }

  check(): void {
    if (this.rightAnswerDelay) return;
    this.answerAttempts++;

    const testMode = this.optionsTrue('testMode');
    const question = this.questions[this.questionNumber];
    const note = question!.note;
    const interval = question!.interval;
    const noteHolder = this.noteHolder;
    const answerNote = noteHolder.notes[0];
    const goalNote = note.getNoteAt(interval) as Note;
    let correct = false;

    if (answerNote !== undefined) {
      correct = goalNote.letter === answerNote.letter;
    }

    // Be sure right # of notes are played:
    if (noteHolder.isEmpty()) {
      this.checkMark!.warn('Remember to draw the answer note on the staff, too!');
      return;
    }

    if (interval.direction === 1 && (answerNote as Note).isLessThan(note)) {
      this.checkMark!.warn('Careful: write an ascending, not descending interval.');
      return;
    }

    if (interval.direction === -1 && (answerNote as Note).isGreaterThan(note)) {
      this.checkMark!.warn('Careful: write a descending, not ascending interval.');
      return;
    }

    this.goalMidi = goalNote.clone().setAccidental('').getMidiNumber();

    if (testMode === true) {
      this.checkpointAnswer(correct);
      this.noteHolder.clearNotes();
      return;
    }

    if (correct === true) {
      this.getAnswerAndData(true);
      this.rightAnswer(1200);
    } else {
      this.getAnswerAndData(false);

      if ((note.getIntervalTo(answerNote as Note) as Interval).isGreaterThan(interval)) {
        this.wrongAnswer('Your interval is too big!');
      } else if ((note.getIntervalTo(answerNote as Note) as Interval).isLessThan(interval)) {
        this.wrongAnswer('Your interval is too small!');
      } else {
        this.wrongAnswer('Right number of half steps, but wrong letter spelling!');
      }
    }
  }

  getAnswerAndData(correct?: boolean): AnswerAndData {
    const correctAnswer = this.questions[this.questionNumber]!.note2;
    const answerAndData = {
      question: `${this.questions[this.questionNumber]!.note} ${
        this.questions[this.questionNumber]!.interval
      }`,
      questionType: 'GenericIntervalID',
      questionText: (this.questionPrompt || '') as string,
      questionSVG: this.editor!.renderer.exportSVG(),
      studentAnswerText: `${this.noteHolder.notes[0]}`,
      correctAnswerText: `${correctAnswer}`,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: [this.questions[this.questionNumber]!.skills],
    };

    return answerAndData;
  }

  gameOver(): void {
    this.instructionsDiv!.innerHTML = 'Nice work!';
    this.instructionsDiv!.setAttribute('role', 'alert');
    this.displayDiv.removeChild(this.lettersDiv!);
    delete this.noteHolder.editable;
    this.editor!.renderer.render();
    super.gameOver();
  }

  playAnswer(): void {
    if (this.note && this.goalMidi) {
      const note1 = this.note.clone().setAccidental('');
      this.player!.play(note1.getMidiNumber(), { startTime: 0, stopTime: 0.5 });
      this.player!.play(this.goalMidi, { startTime: 0.5, stopTime: 1.25 });
    }
  }
}

export default GenericIntervalConstruction;
